import React, { ReactNode } from 'react';
import { truncate } from 'lodash';
import * as yup from 'yup';

import { api } from '@api';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { HookFormTextInput } from '@components/ReactHookFormFields/HookFormTextInput';
import { BODY_MAX_LENGTH, TOPIC_MAX_LENGTH } from '@constants';
import {
  useIncidentCreation,
  UseIncidentCreationProps,
} from '@hooks/incidents';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useURLSearchParams } from '@hooks/useURLSearchParams';
import { BaseIncidentFields } from '@models/api';
import { QUERY_STRINGS } from '@routes/routes.types';

import {
  IncidentFormWrapper,
  IncidentFormWrapperProps,
} from '../IncidentFormWrapper';

type FormProps = {
  title: string;
  description: string;
  files?: File[];
};

export type TitleDescriptionIncidentFormProps = {
  /** Unique identifier for the type of incident */
  incidentKind: string;
  /** Translations coming from Contentful */
  translations?: Record<string, string>;
  /** If true, displays a file input with name `files` */
  withFileUpload?: boolean;
  /** Content rendered before the form */
  preContent?: ReactNode;
  classifications: UseIncidentCreationProps<BaseIncidentFields>['classifications'];
  postIncident?: typeof api.incidents.postGenericIncident;
  className?: string;
  /** Default values for the form */
  defaultValues?: IncidentFormWrapperProps<FormProps>['defaultValues'];
  /** Reactive values for the form. Unlike `defaultValues`, form values will reflect these when changed. */
  values?: IncidentFormWrapperProps<FormProps>['values'];
  /** Whether to disable the title input */
  disableTitleInput?: boolean;
  /** Whether to disable the description input */
  disableDescriptionInput?: boolean;
};

export const TitleDescriptionIncidentForm = ({
  incidentKind,
  translations,
  withFileUpload,
  classifications,
  postIncident = api.incidents.postGenericIncident,
  preContent,
  className,
  defaultValues,
  values,
  disableDescriptionInput,
  disableTitleInput,
}: TitleDescriptionIncidentFormProps) => {
  const { t, tContentful } = useCustomTranslations(translations);

  const { create } = useIncidentCreation({
    postIncident,
    classifications,
    messageTitlePrefix: t('messageTitlePrefix'),
  });

  const { params } = useURLSearchParams<{
    [QUERY_STRINGS.MESSAGE_TITLE]?: string;
    [QUERY_STRINGS.MESSAGE_DESCRIPTION]?: string;
  }>();

  return (
    <IncidentFormWrapper<FormProps>
      incidentKind={incidentKind}
      withFileUpload={withFileUpload}
      defaultValues={{
        ...defaultValues,
        title:
          defaultValues?.title ??
          truncate(params.title, { length: TOPIC_MAX_LENGTH }),
        description:
          defaultValues?.description ??
          truncate(params.description, { length: BODY_MAX_LENGTH }),
      }}
      values={values}
      validationSchema={yup.object().shape({
        title: yup.string().required(),
        description: yup.string().required(),
        files: yup.array().files(tContentful),
      })}
      onSubmit={create}
      className={className}
    >
      {preContent}

      <HookFormTextInput
        type="text"
        name="title"
        label={{ value: t('titleLabel'), type: 'static' }}
        placeholder={t('titlePlaceholder')}
        maxLength={TOPIC_MAX_LENGTH}
        isDisabled={disableTitleInput}
      />

      <HookFormTextArea
        type="text"
        label={{ value: t('descriptionLabel'), type: 'static' }}
        name="description"
        placeholder={t('descriptionPlaceholder')}
        message={t('descriptionHelper')}
        rows={6}
        maxLength={BODY_MAX_LENGTH}
        isDisabled={disableDescriptionInput}
      />
    </IncidentFormWrapper>
  );
};
